import React, { useEffect, useState, useCallback, useRef } from 'react';

import { updateStudy, viewStudy } from '../../../services/StudyService';
import { viewSitesForUser } from '../../../services/StaffMemberService';
import { formatSites } from '../../../util/SiteFormatUtil';

import { BackButton } from '../../common/button/back-button/BackButton';
import { StudyTrialEditGeneralTab } from './general/StudyTrialEditGeneralTab';
import { StudyTrialEditStaffTab } from './staff/StudyTrialEditStaffTab';
import { StudyTrialParticipants } from './participants/StudyTrialParticipants';
import { StudyScheduleTab } from '../study-and-trial-create/visit-schedules/StudyScheduleTab';

import './StudyTrialEdit.css';

export const StudyTrialEdit = ({
  context,
  setContext,
  navigateTo,
  showToaster,
  setSpinnerText,
  toolTipsEnabled,
  navigateToWith,
}) => {
  const [activeTab, setActiveTab] = useState('studyTrialEditGeneral');
  const [study, setStudy] = useState({});
  const [visits, setVisits] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [currentScreen, setCurrentScreen] = useState('studyTrialEditGeneral');

  const tabs = useRef(new Map()).current;

  const populateDropdowns = useCallback(async () => {
    setSpinnerText('Retrieving Facilities');
    try {
      const [responseData, error] = await viewSitesForUser(context.username, context.loggedInUser.externalReference);
      if (responseData) {
        if (responseData.responseStatus === 'Success') {
          const assignedSites = responseData.assignedSites;
          const provinces = formatSites(assignedSites);

          const formattedProvinces = provinces.map((province) => ({
            value: province,
            label: province.province,
          }));

          setProvinces([...formattedProvinces]);
        } else {
          showToaster(responseData);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }
    } catch (error) {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message || 'Error retrieving provinces',
      });
    }
    setSpinnerText(null);
  }, [context.username, context.loggedInUser.externalReference, setSpinnerText, showToaster]);

  const retrieveStudy = useCallback(
    async (username, facilityExternalReference, studyExternalReference) => {
      setSpinnerText('Retrieving Study');
      try {
        const [data, error] = await viewStudy(username, facilityExternalReference, studyExternalReference);

        if (data) {
          if (data.responseStatus === 'Success') {
            showToaster({
              responseStatus: 'Success',
              responseMessage: 'Study Retrieved',
            });
            setStudy(data.study);
            setVisits(data.study.visitPacks);
          } else {
            showToaster(data);
          }
        } else {
          throw new Error(error.message);
        }
      } catch (error) {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message || 'Error retrieving study',
        });
      }
      setSpinnerText(null);
    },
    [setSpinnerText, showToaster]
  );

  useEffect(() => {
    const initialize = async () => {
      await retrieveStudy(
        context.username,
        context.additionalContext.facilityExternalReference,
        context.additionalContext.studyExternalReference
      );
      await populateDropdowns();
    };

    initialize();
  }, [context.additionalContext, context.username, populateDropdowns, retrieveStudy]);

  const handleUpdate = async (studyToUpdate) => {
    setSpinnerText('Updating Study');
    const [data, error] = await updateStudy(context.username, studyToUpdate);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Study Updated',
        });
        await retrieveStudy(context.username, data.facilityExternalReference, data.studyExternalReference);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  tabs.set(
    'studyTrialEditGeneral',
    <StudyTrialEditGeneralTab
      study={study}
      navigateTo={navigateTo}
      context={context}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
      handleUpdate={handleUpdate}
      provinces={provinces}
      editMode
    />
  );
  tabs.set(
    'studyTrialStaff',
    <StudyTrialEditStaffTab
      context={context}
      setContext={setContext}
      navigateTo={navigateTo}
      study={study}
      handleUpdate={handleUpdate}
    />
  );
  tabs.set(
    'studySchedules',
    <StudyScheduleTab
      context={context}
      setCurrentScreen={setCurrentScreen}
      setContext={setContext}
      setActiveTab={setActiveTab}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
      toolTipsEnabled={toolTipsEnabled}
      navigateTo={navigateTo}
      visits={visits}
      setVisits={setVisits}
      study={study}
      handleUpdate={handleUpdate}
      editMode
    />
  );
  tabs.set(
    'studyTrialParticipants',
    <StudyTrialParticipants context={context} setContext={setContext} navigateToWith={navigateToWith} study={study} />
  );

  const handleBack = () => {
    navigateTo('studyTrialAdmin');
  };

  const handleNavigationClick = (tab) => {
    setActiveTab(tab);
    setCurrentScreen(tab);
  };

  return (
    <div className="study-trial-edit-container">
      <div className="study-trial-edit-header-container">
        <BackButton onClick={handleBack} />
        <h2>Edit/View Study / Clinical Trial Registration</h2>
      </div>

      <div className="wizard-content-container">
        <div className="wizard-main-content-container">
          <div className="clients-create-container">
            <div className="clients-create-tab-container">
              <div
                className={
                  'study-trial-edit-tab general ' + (activeTab === 'studyTrialEditGeneral' ? 'active-tab' : null)
                }
                onClick={() => handleNavigationClick('studyTrialEditGeneral')}
              >
                General
              </div>
              <div
                className={'study-trial-edit-tab schedules ' + (activeTab === 'studySchedules' ? 'active-tab' : null)}
                onClick={() => handleNavigationClick('studySchedules')}
              >
                Visit Schedule
              </div>
              <div
                className={'study-trial-edit-tab staff ' + (activeTab === 'studyTrialStaff' ? 'active-tab' : null)}
                onClick={() => handleNavigationClick('studyTrialStaff')}
              >
                Staff
              </div>
              <div
                className={
                  'study-trial-edit-tab participants ' + (activeTab === 'studyTrialParticipants' ? 'active-tab' : null)
                }
                onClick={() => handleNavigationClick('studyTrialParticipants')}
              >
                Participants
              </div>
            </div>
            <div className={'study-trial-edit-tab-content ' + activeTab}>{tabs.get(currentScreen)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
